
import { ReactNode, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { getAbsoluteUrl } from "@/utils/domainUtils";

interface ProtectedRouteProps {
  children: ReactNode;
}

// Enhanced loading fallback with better user experience
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="max-w-md w-full bg-white p-6 rounded-lg shadow-lg text-center">
      <h2 className="text-xl font-bold mb-2 text-gray-800">City & Sanctuary</h2>
      <div className="flex items-center justify-center mb-6">
        <div className="animate-spin h-8 w-8 border-4 border-gray-400 border-t-transparent rounded-full mr-3"></div>
        <p className="text-gray-600">Verifying authentication...</p>
      </div>
      <div className="text-sm text-gray-500 mb-4">
        <p>This is taking longer than usual.</p>
        <p>If you continue to see this screen, please try refreshing or logging in again.</p>
      </div>
      <div className="space-y-2">
        <button 
          onClick={() => window.location.reload()} 
          className="block w-full px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
        >
          Refresh Page
        </button>
        <a href={getAbsoluteUrl('/login')} className="block w-full px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-800 transition-colors text-center">
          Go to Login
        </a>
      </div>
    </div>
  </div>
);

export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [authCheckTimeout, setAuthCheckTimeout] = useState(false);
  
  // Enhanced auth check with timeout fallback
  useEffect(() => {
    console.log("Protected route at", location.pathname, "- Auth state:", { isAuthenticated, isLoading });
    
    // Set a timeout to handle cases where auth check hangs
    const timeoutId = setTimeout(() => {
      if (isLoading) {
        console.log("Auth check timeout reached, showing timeout state");
        setAuthCheckTimeout(true);
      }
    }, 10000); // 10 second timeout
    
    // Wait for auth to finish loading before making any decisions
    if (!isLoading) {
      clearTimeout(timeoutId);
      setAuthCheckTimeout(false);
      
      if (!isAuthenticated) {
        console.log("Auth check complete - User not authenticated, will redirect to login");
        setShouldRedirect(true);
      }
    }
    
    return () => clearTimeout(timeoutId);
  }, [isAuthenticated, isLoading, location.pathname]);

  // Show enhanced loading or timeout state while checking auth
  if (isLoading && !authCheckTimeout) {
    console.log("Auth still loading, showing loading state");
    return <LoadingFallback />;
  }
  
  // Show timeout state if auth check is taking too long
  if (authCheckTimeout) {
    console.log("Auth check timeout, showing enhanced loading with options");
    return <LoadingFallback />;
  }
  
  // Redirect to login if not authenticated
  if (shouldRedirect) {
    console.log("Redirecting to login from", location.pathname);
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  
  // Only render children if authenticated
  if (isAuthenticated) {
    console.log("User authenticated, rendering protected content");
    return (
      <ErrorBoundary>
        {children}
      </ErrorBoundary>
    );
  }
  
  // Fallback loading state - this should rarely be reached
  console.log("Unexpected auth state - showing loading fallback");
  return <LoadingFallback />;
};
